import type { VirtualItem } from '@tanstack/react-virtual';
import type { QuoteConcept } from '../api/quotes.js';

/**
 * Agrupadores "pegados" (sticky) apilados por jerarquía sobre el grid del
 * presupuesto. Al scrollear, las partidas/subpartidas del concepto que está
 * arriba quedan fijas bajo el header de columnas; cuando entra el siguiente
 * hermano, EMPUJA al saliente hacia arriba (estilo iOS). Soporta todos los
 * niveles (catálogos tipo CONAGUA traen hasta ~5).
 *
 * Por qué un overlay manual y no `position:sticky` de CSS: el grid es
 * virtualizado — las filas de agrupador se DESMONTAN al salir de la ventana,
 * así que un sticky nativo perdería el header apenas te alejas de él. Aquí
 * calculamos, desde el scroll actual, la cadena de ancestros del concepto que
 * cruza cada "ranura" y los pintamos pinneados.
 *
 * DE DÓNDE SALE "EN QUÉ PARTIDA ESTOY" (2026-09-01): del ORDEN DEL DOCUMENTO,
 * o sea del agrupador más cercano hacia arriba en la misma lista que se ve en
 * pantalla. Antes se subía por la cadena `parentIdx` de cada concepto, y eso
 * dejaba la barra en blanco cada vez que el lector del catálogo no había atado
 * los conceptos a su partida: medido en el presupuesto MENATY (5 partidas, 131
 * conceptos) 80 conceptos venían con `parentIdx` vacío y los otros 51 apuntando
 * a OTRO CONCEPTO —uno de ellos a sí mismo—, así que la cadena terminaba vacía
 * para los 131 y el overlay no pintaba nunca. El orden del documento es el
 * mismo que el usuario está leyendo y no depende de que ese dato venga bien.
 * `parentIdx` se sigue usando, pero solo para el NIVEL de un agrupador
 * (partida vs subpartida), que es una relación agrupador→agrupador.
 *
 * Coordenadas: `screenY` de una fila (relativo al tope del área scrolleable) =
 * `virtualItem.start - scrollOffset`. El contenedor va `sticky top:theadH`, así
 * que un hijo con `top:0` cae justo bajo el header de columnas.
 */

// Alto uniforme de cada agrupador pinneado (compacto; no necesita igualar la
// altura real de la fila, es una miga de pan fija).
const H = 32;

interface Props {
  /** Filas visibles (filtered), en orden: agrupadores + conceptos. */
  rows: QuoteConcept[];
  /** Profundidad por idx (cadena parentIdx) — 0 = partida raíz. */
  depthByIdx: Map<number, number>;
  /** Ítems renderizados por el virtualizador (posiciones). */
  virtualItems: VirtualItem[];
  /** scrollTop del área scrolleable. */
  scrollOffset: number;
  /** Alto del header de columnas (thead), medido. El overlay pinnea debajo. */
  theadH: number;
}

// Colores por nivel: variables theme-aware del design system (pastel en claro,
// tinte sutil en oscuro). Niveles 0-5; 6+ comparte la ranura más profunda.
function bgForDepth(depth: number): string {
  return `var(--group-bg-${Math.min(depth, 5)})`;
}
function colorForDepth(depth: number): string {
  return `var(--group-fg-${Math.min(depth, 5)})`;
}

export function StickyGroupHeaders({
  rows, depthByIdx, virtualItems, scrollOffset, theadH,
}: Props) {
  if (theadH <= 0 || virtualItems.length === 0 || rows.length === 0) return null;

  // Posición de cada fila renderizada, por índice en `rows`.
  const posByIndex = new Map<number, VirtualItem>();
  for (const vi of virtualItems) posByIndex.set(vi.index, vi);
  const screenTopOf = (index: number): number | null => {
    const vi = posByIndex.get(index);
    return vi ? vi.start - scrollOffset : null;
  };
  // Fila (índice en `rows`) que cruza una `y` de pantalla.
  const rowIndexAtScreenY = (y: number): number | null => {
    for (const vi of virtualItems) {
      const top = vi.start - scrollOffset;
      if (top <= y && y < top + vi.size) return vi.index;
    }
    return null;
  };

  // idx → posición en `rows` (para buscar el siguiente hermano).
  const posInRowsByIdx = new Map<number, number>();
  for (let i = 0; i < rows.length; i++) posInRowsByIdx.set(rows[i]!.idx, i);

  // Cadena de agrupadores que CONTIENEN a la fila de la posición `pos`, leída
  // del orden del documento: hacia arriba, el agrupador más cercano; después el
  // más cercano de un nivel más somero, y así hasta la partida raíz. Devuelve
  // de somero a profundo (el [0] es la partida). Si la fila ES un agrupador, se
  // incluye a sí misma. No depende de `parentIdx` de los conceptos.
  interface Eslabon { group: QuoteConcept; depth: number }
  const cadenaCache = new Map<number, Eslabon[]>();
  const cadenaEnPos = (pos: number): Eslabon[] => {
    const hit = cadenaCache.get(pos);
    if (hit) return hit;
    const out: Eslabon[] = [];
    let masSomeroVisto = Number.POSITIVE_INFINITY;
    for (let i = pos; i >= 0; i--) {
      const r = rows[i];
      if (!r || r.kind !== 'group') continue;
      const d = depthByIdx.get(r.idx) ?? 0;
      if (d >= masSomeroVisto) continue; // hermano o descendiente: no me contiene
      out.push({ group: r, depth: d });
      masSomeroVisto = d;
      if (d === 0) break; // llegamos a la partida raíz
    }
    out.reverse();
    cadenaCache.set(pos, out);
    return out;
  };

  interface Slot { key: string; depth: number; group: QuoteConcept; y: number }
  const slots: Slot[] = [];
  for (let k = 0; k < 16; k++) {
    const foldY = theadH + k * H + 1; // +1: dentro de la ranura, no en el borde
    const ridx = rowIndexAtScreenY(foldY);
    if (ridx == null) break;
    const eslabon = cadenaEnPos(ridx)[k];
    if (!eslabon) break; // la fila no tiene ancestro a esta altura de la pila → listo
    const g = eslabon.group;

    // Empuje: cuando el siguiente agrupador de nivel ≤ al suyo se acerca a la
    // ranura, el saliente se desliza hacia arriba (y queda detrás del ancestro).
    let y = theadH + k * H;
    const gPos = posInRowsByIdx.get(g.idx);
    if (gPos != null) {
      for (let j = gPos + 1; j < rows.length; j++) {
        const r = rows[j]!;
        if (r.kind === 'group' && (depthByIdx.get(r.idx) ?? 0) <= eslabon.depth) {
          const nextY = screenTopOf(j); // null si no está renderizado (lejos)
          if (nextY != null) y = Math.min(y, nextY - H);
          break;
        }
      }
    }
    slots.push({ key: g.id, depth: k, group: g, y });
  }

  if (slots.length === 0) return null;

  return (
    <div style={{ position: 'sticky', top: theadH, height: 0, zIndex: 3, pointerEvents: 'none' }} aria-hidden>
      {slots.map((s) => (
        <div
          key={s.key}
          style={{
            position: 'absolute', left: 0, right: 0, top: s.y - theadH, height: H,
            display: 'flex', alignItems: 'center', gap: 'var(--space-s)',
            paddingLeft: `${14 + s.depth * 16}px`, paddingRight: 14,
            background: bgForDepth(s.depth), color: colorForDepth(s.depth),
            borderBottom: '1px solid var(--border-subtle)', boxSizing: 'border-box',
            // Sombra sutil solo en el más profundo para separar del contenido.
            boxShadow: s.depth === slots.length - 1 ? '0 4px 8px -6px rgba(16,24,40,0.35)' : undefined,
            overflow: 'hidden',
            // Ancestros más someros por encima → el saliente se mete DETRÁS.
            zIndex: 100 - s.depth,
          }}
        >
          {s.group.clave && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: s.depth === 0 ? 12.5 : 11.5, fontWeight: 700, flexShrink: 0 }}>
              {s.group.clave}
            </span>
          )}
          <span style={{
            fontSize: s.depth === 0 ? 13 : 12, fontWeight: s.depth === 0 ? 700 : 600,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {s.group.descripcion}
          </span>
        </div>
      ))}
    </div>
  );
}
